import { useEffect, useRef } from 'react'
import { SWRConfiguration } from 'swr'
import { SwrUseQueryOutput, useQuery } from '../use-query'
import {
    Products,
    Aggregation,
    Maybe,
    ProductSearchArgs
} from '@pylot-data/pylotschema'
import { updateSearchInputCtx } from './utils/updateSearchInputCtx'
import { updateSearchResultsCtx } from './utils/updateSearchResultsCtx'
import { v4 as uuidv4 } from 'uuid'

import { productSearchSuggestionQuery2 } from './graphql/productSearchSuggestionQuery'

type ProductResultWithData = {
    productSearch: Products
    aggregations?: Maybe<Array<Maybe<Aggregation>>>
}

type UseSearchResult = {
    products: Products
    aggregations?: Maybe<Array<Maybe<Aggregation>>>
} & Omit<SwrUseQueryOutput<ProductResultWithData>, 'data'>

export const useSearchSuggestion = (
    searchTerm: string,
    swrOptions: SWRConfiguration = {},
    isActive = true
): UseSearchResult => {
    const variables = {
        phrase: searchTerm,
        page_size: 10,
        current_page: 1
    }

    const hasRun = useRef(false)
    const customPylotQueryHeader = 'products'

    const SEARCH_UNIT_ID = 'livesearch-popover'
    let magentoStorefrontEvtPublish

    const { data, ...rest } = useQuery<
        ProductSearchArgs,
        ProductResultWithData
    >(
        isActive ? productSearchSuggestionQuery2 : null,
        variables,
        swrOptions,
        undefined,
        undefined,
        customPylotQueryHeader
    )

    useEffect(() => {
        hasRun.current = false
    }, [searchTerm])

    const products = data?.data?.productSearch!
    // const aggregations = data?.data?.productSearch?.aggregations

    // ======  initialize search request and result data collection =====
    useEffect(() => {
        // Prefer the real ID from the Live Search backend; fall back to a client-side uuid
        const searchRequestId = (products as any)?.search_request_id || uuidv4()

        if (typeof window !== 'undefined' && products && !hasRun.current) {
            hasRun.current = true

            updateSearchInputCtx(
                SEARCH_UNIT_ID,
                searchRequestId,
                searchTerm,
                [],
                20,
                1,
                []
            )

            magentoStorefrontEvtPublish =
                //eslint-disable-next-line
                window.magentoStorefrontEvents?.publish

            //eslint-disable-next-line
            magentoStorefrontEvtPublish?.searchRequestSent &&
                magentoStorefrontEvtPublish.searchRequestSent(SEARCH_UNIT_ID)

            updateSearchResultsCtx(
                SEARCH_UNIT_ID,
                searchRequestId,
                products,
                20
            )

            //eslint-disable-next-line
            magentoStorefrontEvtPublish?.searchResponseReceived &&
                magentoStorefrontEvtPublish.searchResponseReceived(
                    SEARCH_UNIT_ID
                )

            //eslint-disable-next-line
            magentoStorefrontEvtPublish?.searchResultsView &&
                magentoStorefrontEvtPublish.searchResultsView(SEARCH_UNIT_ID)

            // Persist for addToCart / placeOrder attribution
            console.log('[LiveSearch][Popover] Persisting IDs:', {
                searchRequestId,
                SEARCH_UNIT_ID
            })
            sessionStorage.setItem('lastSearchRequestId', searchRequestId)
            sessionStorage.setItem('lastSearchUnitId', SEARCH_UNIT_ID)
        }
    }, [products])
    // ======  end of data collection =====

    return {
        products,
        aggregations: [],
        ...rest
    }
}
